import React, { useState, useEffect, useCallback } from "react";
import {
  Search,
  Building,
  Calendar,
  Download,
  Filter,
  X,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import SSTFormatDetails from "./SSTFormatDetails";
import ExportTypeModal from "./ExportTypeModal";
import {
  generateMISCSVContent,
  generateSummaryCSVContent,
  triggerCSVDownload,
  mapSSTDetailsData,
  generateMISRows,
  generateSummaryRows,
  triggerExcelDownload,
} from "./sstExportUtils";
import { getExportFileName } from "../../utils/fileExportNaming";
import { useToast } from "../shared/Toast";
import { getAllClientFormats, getAllClients } from "../../api/createClientApi";
import { getSstFormatByClientId } from "../../api/sstFormatapi";
import CustomSelect from "../shared/CustomSelect";

/* ─── Filter Panel for List Page ─────────────────────────────────────── */
const FilterPanel = ({ isOpen, onClose, onApply, clients = [] }) => {
  const [clientName, setClientName] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  if (!isOpen) return null;

  const handleApply = () => {
    onApply({ clientName, startDate, endDate });
  };

  const handleReset = () => {
    setClientName("");
    setStartDate("");
    setEndDate("");
    onApply({ clientName: "", startDate: "", endDate: "" });
  };

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1000,
        }}
      />
      <div
        style={{
          position: "absolute",
          top: "60px",
          right: "20px",
          width: "280px",
          backgroundColor: "white",
          borderRadius: "12px",
          padding: "20px",
          zIndex: 1001,
          boxShadow: "0 10px 40px rgba(0, 0, 0, 0.15)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: "8px",
            marginBottom: "20px",
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: "18px",
              fontWeight: 600,
              color: "#20B967",
            }}
          >
            Filters
          </h2>
          <span style={{ fontSize: "13px", color: "#555" }}>Filter & Sort</span>
        </div>

        {/* Date Range */}
        <div style={{ marginBottom: "16px" }}>
          <label
            style={{
              display: "block",
              fontSize: "12px",
              fontWeight: 600,
              color: "#333",
              marginBottom: "8px",
              textTransform: "uppercase",
            }}
          >
            Date Range
          </label>
          <div style={{ display: "flex", gap: "8px" }}>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={{
                flex: 1,
                padding: "8px",
                border: "1px solid #C5C5C5",
                borderRadius: "6px",
                fontSize: "13px",
              }}
              placeholder="From"
            />
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={{
                flex: 1,
                padding: "8px",
                border: "1px solid #C5C5C5",
                borderRadius: "6px",
                fontSize: "13px",
              }}
              placeholder="To"
            />
          </div>
        </div>

        {/* Client Selection */}
        <div style={{ marginBottom: "16px" }}>
          <label
            style={{
              display: "block",
              fontSize: "12px",
              fontWeight: 600,
              color: "#333",
              marginBottom: "8px",
              textTransform: "uppercase",
            }}
          >
            Select Client
          </label>
          <CustomSelect
            value={clientName}
            onChange={(val) => setClientName(val)}
            options={[
              { value: "", label: "All Clients" },
              ...clients.map((c) => ({ value: c, label: c }))
            ]}
            placeholder="All Clients"
            searchable
            width="100%"
          />
        </div>

        {/* Actions */}
        <div style={{ display: "flex", gap: "8px", marginTop: "20px" }}>
          <button
            onClick={handleReset}
            style={{
              flex: 1,
              padding: "10px",
              border: "1px solid #C5C5C5",
              borderRadius: "6px",
              backgroundColor: "white",
              color: "#555",
              fontSize: "14px",
              cursor: "pointer",
            }}
          >
            Reset
          </button>
          <button
            onClick={handleApply}
            style={{
              flex: 2,
              padding: "10px",
              border: "none",
              borderRadius: "6px",
              backgroundColor: "#20B967",
              color: "white",
              fontSize: "14px",
              cursor: "pointer",
            }}
          >
            Apply
          </button>
        </div>
      </div>
    </>
  );
};

const TopBar = ({ onSearchChange, onClearSearch, searchTerm = "", onOpenFilters }) => {
  const [hoverFilter, setHoverFilter] = useState(false);

  return (
    <div
      style={{
        backgroundColor: "#EEF8F3",
        borderRadius: "8px",
        padding: "12px 20px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        position: "relative",
      }}
    >
      <h2
        style={{ fontSize: "18px", fontWeight: 600, color: "#333", margin: 0 }}
      >
        Trip Sheets
      </h2>
      <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            backgroundColor: "#F0EEF6",
            borderRadius: "20px",
            padding: "10px 16px",
            width: "250px",
          }}
        >
          <input
            type="text"
            placeholder="Search Here"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            style={{
              border: "none",
              background: "transparent",
              outline: "none",
              flexGrow: 1,
              fontFamily: "inherit",
              color: "#333",
              fontSize: "14px",
            }}
          />
          {searchTerm ? (
            <X
              size={16}
              color="#777"
              style={{ cursor: "pointer", marginLeft: "4px" }}
              onClick={onClearSearch}
              title="Clear search"
            />
          ) : (
            <Search size={18} color="#555" />
          )}
        </div>
        <button
          onClick={onOpenFilters}
          onMouseEnter={() => setHoverFilter(true)}
          onMouseLeave={() => setHoverFilter(false)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "10px 16px",
            border: "1px solid #C5C5C5",
            borderRadius: "6px",
            backgroundColor: hoverFilter ? "#f0f9ff" : "white",
            color: "#555",
            fontSize: "14px",
            cursor: "pointer",
            transition: "background-color 0.2s",
          }}
        >
          <Filter size={16} />
          Filters
        </button>
      </div>
    </div>
  );
};

const SSTSheetRow = ({ company, date, onViewDetails, onDownload }) => {
  const [hoverView, setHoverView] = useState(false);
  const [hoverDownload, setHoverDownload] = useState(false);

  return (
    <div
      style={{
        backgroundColor: "#FFFFFF",
        borderRadius: "8px",
        padding: "12px 20px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: "8px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.02)",
      }}
    >
      <div
        style={{ display: "flex", alignItems: "center", gap: "24px", flex: 1 }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            width: "200px",
          }}
        >
          <Building size={18} color="#20B967" />
          <span style={{ fontSize: "15px", color: "#333", fontWeight: 500 }}>
            {company}
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Calendar size={18} color="#20B967" />
          <span style={{ fontSize: "15px", color: "#555" }}>{date}</span>
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <button
          onClick={onViewDetails}
          onMouseEnter={() => setHoverView(true)}
          onMouseLeave={() => setHoverView(false)}
          style={{
            backgroundColor: hoverView ? "#dcf3e6" : "#E5F8ED",
            color: "#20B967",
            border: "none",
            borderRadius: "20px",
            padding: "8px 20px",
            cursor: "pointer",
            fontSize: "13px",
            fontWeight: 500,
            transition: "background-color 0.2s",
          }}
        >
          View Details
        </button>
        <button
          onClick={onDownload}
          onMouseEnter={() => setHoverDownload(true)}
          onMouseLeave={() => setHoverDownload(false)}
          style={{
            backgroundColor: hoverDownload ? "#e0f2fe" : "#F0F9FF",
            color: "#0EA5E9",
            border: "none",
            borderRadius: "50%",
            width: "36px",
            height: "36px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            transition: "background-color 0.2s",
          }}
        >
          <Download size={16} />
        </button>
      </div>
    </div>
  );
};

const SSTFormat = () => {
  const toast = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const [viewingDetails, setViewingDetails] = useState(false);
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState({
    clientName: "",
    startDate: "",
    endDate: "",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(10);
  const [pagination, setPagination] = useState({
    totalCount: 0,
    totalPages: 1,
    currentPage: 1,
    limit: 10,
  });
  const [allClients, setAllClients] = useState([]);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [activeExportItem, setActiveExportItem] = useState(null);

  // Debounce search query by 300ms to avoid network race conditions
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch client options for filter dropdown
  useEffect(() => {
    const fetchClientsList = async () => {
      try {
        const res = await getAllClients(1, 1000);
        const clientList = res.data || res || [];
        const names = clientList.map((c) => c.clientName).filter(Boolean);
        setAllClients([...new Set(names)]);
      } catch (err) {
        console.error("Failed to load clients for filter dropdown", err);
      }
    };
    fetchClientsList();
  }, []);

  const fetchClientFormats = useCallback(
    async (page = currentPage, search = debouncedSearch, currentFilters = filters) => {
      setIsLoading(true);
      setError("");
      try {
        const response = await getAllClientFormats(
          page,
          limit,
          search,
          currentFilters
        );
        const formats = response.data || [];
        const mappedData = formats.map((item) => ({
          id: item.id || item.formatId || item.clientFormatId,
          company: item.clientName || item.client?.clientName || "Unknown Client",
          date: item.createdAt || item.uploadDate
            ? new Date(item.createdAt || item.uploadDate).toLocaleDateString(
                "en-IN",
                {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                }
              )
            : new Date().toLocaleDateString("en-IN", {
                day: "numeric",
                month: "short",
                year: "numeric",
              }),
        }));
        setData(mappedData);
        setPagination({
          totalCount: response.totalCount || mappedData.length,
          totalPages: response.totalPages || 1,
          currentPage: response.currentPage || page,
          limit: response.limit || limit,
        });
      } catch (err) {
        setError("Failed to load trip sheets");
      } finally {
        setIsLoading(false);
      }
    },
    [currentPage, limit, debouncedSearch, filters]
  );

  useEffect(() => {
    fetchClientFormats(currentPage, debouncedSearch, filters);
  }, [fetchClientFormats, currentPage, debouncedSearch, filters]);

  // Real-time client-side filter fallback across company name & date
  const filteredData = React.useMemo(() => {
    if (!searchTerm.trim()) return data;
    const term = searchTerm.toLowerCase().trim();
    return data.filter((item) => {
      const matchCompany = item.company?.toLowerCase().includes(term);
      const matchDate = item.date?.toLowerCase().includes(term);
      return matchCompany || matchDate;
    });
  }, [data, searchTerm]);

  useEffect(() => {
    // Check if navigation state has data from ClientFormat
    if (location.state?.fromClientFormat && location.state?.formatId) {
      setSelectedSheet({
        id: location.state.formatId,
        company: location.state.company,
        date: location.state.date,
      });
      setViewingDetails(true);
      // Clear the state so it doesn't re-trigger on refresh
      navigate("/sst-format", { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  if (viewingDetails) {
    return (
      <SSTFormatDetails
        company={selectedSheet?.company}
        date={selectedSheet?.date}
        formatId={selectedSheet?.id}
        onBack={() => setViewingDetails(false)}
      />
    );
  }

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const handleApplyFilters = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handleOpenExportModal = (item) => {
    setActiveExportItem(item);
    setExportModalOpen(true);
  };

  const processDownload = async (exportType, fileFormat = "xlsx") => {
    if (!activeExportItem || !activeExportItem.id) {
      toast("No format ID available for download", "error");
      return;
    }

    try {
      const response = await getSstFormatByClientId(activeExportItem.id);
      const sstData = response.data || response || [];

      if (sstData.length === 0) {
        toast("No trip data available for download", "error");
        return;
      }

      // Map data same as SSTFormatDetails
      const detailsData = mapSSTDetailsData(sstData);

      const fileName = getExportFileName({
        reportName: exportType,
        data: detailsData,
        fromDate: filters.startDate,
        toDate: filters.endDate,
        fallbackDate: activeExportItem.date,
        ext: fileFormat,
      });

      if (exportType === "Summary") {
        if (fileFormat === "xlsx") {
          const tableData = generateSummaryRows(
            detailsData,
            activeExportItem.date
          );
          triggerExcelDownload(tableData, fileName, "Summary");
        } else {
          const csvContent = generateSummaryCSVContent(
            detailsData,
            activeExportItem.date
          );
          triggerCSVDownload(csvContent, fileName);
        }
      } else {
        if (fileFormat === "xlsx") {
          const tableData = generateMISRows(detailsData);
          triggerExcelDownload(tableData, fileName, "MIS");
        } else {
          const csvContent = generateMISCSVContent(detailsData);
          triggerCSVDownload(csvContent, fileName);
        }
      }
      toast(`${exportType} downloaded successfully!`, "success");
    } catch (err) {
      console.error("Download error:", err);
      toast("Failed to download trip sheet. Please try again.", "error");
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        position: "relative",
      }}
    >
      <TopBar
        searchTerm={searchTerm}
        onSearchChange={handleSearch}
        onClearSearch={() => handleSearch("")}
        onOpenFilters={() => setIsFilterOpen(true)}
      />
      <FilterPanel
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        onApply={handleApplyFilters}
        clients={allClients.length > 0 ? allClients : [...new Set(data.map((item) => item.company))]}
      />
      {searchTerm && (
        <div
          style={{
            padding: "8px 16px",
            backgroundColor: "#f0f9ff",
            border: "1px solid #bae6fd",
            borderRadius: "6px",
            fontSize: "14px",
            color: "#0369a1",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>
            Found {filteredData.length} compan{filteredData.length !== 1 ? "ies" : "y"}{" "}
            matching "{searchTerm}"
          </span>
          <button
            onClick={() => handleSearch("")}
            style={{
              background: "none",
              border: "none",
              color: "#0369a1",
              cursor: "pointer",
              fontSize: "13px",
              fontWeight: 500,
              textDecoration: "underline",
            }}
          >
            Clear
          </button>
        </div>
      )}
      {isLoading && (
        <div
          style={{
            padding: "40px",
            textAlign: "center",
            color: "#666",
            fontSize: "14px",
            backgroundColor: "white",
            borderRadius: "8px",
          }}
        >
          Loading trip sheets...
        </div>
      )}
      {error && (
        <div
          style={{
            padding: "12px 16px",
            backgroundColor: "#FEE2E2",
            color: "#DC2626",
            borderRadius: "6px",
            fontSize: "14px",
          }}
        >
          {error}
        </div>
      )}
      {!isLoading && !error && (
        <div style={{ display: "flex", flexDirection: "column" }}>
          {filteredData.length > 0 ? (
            filteredData.map((item) => (
              <SSTSheetRow
                key={item.id || item.company}
                company={item.company}
                date={item.date}
                onViewDetails={() => {
                  setSelectedSheet(item);
                  setViewingDetails(true);
                }}
                onDownload={() => handleOpenExportModal(item)}
              />
            ))
          ) : (
            <div
              style={{
                padding: "40px",
                textAlign: "center",
                color: "#666",
                fontSize: "14px",
                backgroundColor: "white",
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.02)",
              }}
            >
              {searchTerm
                ? `No company found matching "${searchTerm}"`
                : "No trip sheets available"}
            </div>
          )}
        </div>
      )}

      {/* Pagination Bar */}
      {!isLoading && !error && data.length > 0 && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "16px 20px",
            backgroundColor: "white",
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.02)",
          }}
        >
          <div style={{ fontSize: "14px", color: "#666" }}>
            Showing{" "}
            {(pagination.currentPage - 1) * pagination.limit + 1} to{" "}
            {Math.min(
              pagination.currentPage * pagination.limit,
              pagination.totalCount
            )}{" "}
            of {pagination.totalCount} entries
          </div>
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <button
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={pagination.currentPage === 1}
              style={{
                padding: "8px 14px",
                borderRadius: "6px",
                border: "1px solid #D1D5DB",
                backgroundColor:
                  pagination.currentPage === 1 ? "#F3F4F6" : "white",
                color: pagination.currentPage === 1 ? "#9CA3AF" : "#374151",
                cursor:
                  pagination.currentPage === 1 ? "not-allowed" : "pointer",
                fontSize: "14px",
                fontWeight: 500,
              }}
            >
              Previous
            </button>
            {Array.from(
              { length: pagination.totalPages },
              (_, i) => i + 1
            ).map((page) => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                style={{
                  padding: "8px 14px",
                  borderRadius: "6px",
                  border: "1px solid #D1D5DB",
                  backgroundColor:
                    pagination.currentPage === page ? "#20B967" : "white",
                  color: pagination.currentPage === page ? "white" : "#374151",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: 500,
                }}
              >
                {page}
              </button>
            ))}
            <button
              onClick={() =>
                setCurrentPage((prev) =>
                  Math.min(pagination.totalPages, prev + 1)
                )
              }
              disabled={pagination.currentPage >= pagination.totalPages}
              style={{
                padding: "8px 14px",
                borderRadius: "6px",
                border: "1px solid #D1D5DB",
                backgroundColor:
                  pagination.currentPage >= pagination.totalPages
                    ? "#F3F4F6"
                    : "white",
                color:
                  pagination.currentPage >= pagination.totalPages
                    ? "#9CA3AF"
                    : "#374151",
                cursor:
                  pagination.currentPage >= pagination.totalPages
                    ? "not-allowed"
                    : "pointer",
                fontSize: "14px",
                fontWeight: 500,
              }}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Export Type Selector Modal */}
      <ExportTypeModal
        isOpen={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        onConfirm={processDownload}
        companyName={activeExportItem?.company || "Trip Sheet"}
      />
    </div>
  );
};

export default SSTFormat;
